import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { APPLY_UPDATER } from "../store/actionTypes";

// Shared banner list (stale-while-revalidate).
// - Render immediately from the Redux store, or from the copy kept in
//   localStorage on a fresh page load, so banners never wait on the API.
// - Refresh in the background: concurrent callers share one request, and
//   it re-runs at most once per REVALIDATE_MS while the site is open.
// - Only touch the store when the response actually differs, so banners
//   (and their carousels) don't re-render for nothing.

const CACHE_KEY = "bannerlist_cache";
const REVALIDATE_MS = 60 * 1000;

const readCache = () => {
  try {
    const list = JSON.parse(localStorage.getItem(CACHE_KEY));
    return Array.isArray(list) ? list : null;
  } catch {
    return null;
  }
};

let latest = readCache();
let latestJson = latest ? JSON.stringify(latest) : "";
let inflight = null;
let lastFetchedAt = 0;

const revalidate = () => {
  if (inflight) return inflight;
  if (Date.now() - lastFetchedAt < REVALIDATE_MS) return Promise.resolve(null);
  inflight = api
    .getBannerlist()
    .then((res) => {
      lastFetchedAt = Date.now();
      return res && Array.isArray(res.data) ? res.data : null;
    })
    .catch(() => null)
    .finally(() => {
      inflight = null;
    });
  return inflight;
};

const setBanners = (dispatch, list) =>
  dispatch({
    type: APPLY_UPDATER,
    updater: (state) => ({ ...state, bannerlist: list }),
  });

export default function useBanners() {
  const dispatch = useDispatch();
  const stored = useSelector((state) => state.bannerlist);

  useEffect(() => {
    if (!stored && latest) setBanners(dispatch, latest);
    revalidate().then((list) => {
      if (!list) return;
      const json = JSON.stringify(list);
      if (json === latestJson) return;
      latest = list;
      latestJson = json;
      try {
        localStorage.setItem(CACHE_KEY, json);
      } catch {
        // storage full / blocked: the in-memory copy is enough
      }
      setBanners(dispatch, list);
    });
  }, []);

  return stored || latest || [];
}

// Visible banners for one page ("home", "product", ...)
export const pageBanners = (list, page) =>
  list.filter((o) => o.page === page && o.display === 1);
